import type { SurfaceToken } from "../../components/ui/box-classes";
import type { IconSource } from "../../components/ui/icon";
import type { Color } from "../../lib/colors";
import * as React from "react";
import type { Omittable } from "../../lib/props";

export type EntityAvatarConfig = Omittable<{
  src?: string;
  fallback?: string;
  icon?: IconSource;
  /** Color name from the shared scale. Narrow stored strings with `toColor`. */
  color?: Color;
  /** When true, renders the icon in a padded rounded box instead of inside an avatar circle. */
  padded?: boolean;
  /** Custom render function for the avatar. When provided, overrides all other avatar properties. */
  render?: () => React.ReactNode;
}>;

export type EntityItemConfig = Omittable<{
  id: string;
  title: string;
  description?: string;
  /** Section this item sits under. A run of consecutive items sharing a
   *  section renders one group heading above it, and a pool whose items
   *  carry sections suppresses its own pool-label header (the sections ARE
   *  the headers). Search filters items as usual — a section with no matches
   *  disappears with them. */
  section?: string;
  /** Optional SECOND grouping level nested under `section` — a run of
   *  consecutive items sharing a (section, subsection) pair renders an indented
   *  sub-heading above it. With `collapsibleSections` each level is
   *  independently collapsible: closing the section hides its subsections and
   *  their items; closing a subsection hides only its own items.
   *  Requires `section`; ignored without one. */
  subsection?: string;
  /** Hover tooltip on the item row — explanation text that shouldn't occupy
   *  an inline description line (e.g. dynamic-reference entries). */
  tooltip?: string;
  avatar?: EntityAvatarConfig;
  /** A persistent fact in the row's LEADING column, where the avatar would
   *  sit. For a list whose rows all carry the SAME avatar — a column of
   *  identical glyphs is noise, and the eye needs the one visual that differs
   *  row to row. Pair it with the pool's `hideAvatars`, which frees the
   *  column; the `avatar` config stays as data, because the trigger and the
   *  chips still render the entity's own visual once it is picked. Like
   *  `rowMeta` it never hover-gates and its clicks reach the row's own toggle. */
  rowLead?: () => React.ReactNode;
  /** Persistent facts at the row's right edge — a group of people, a status
   *  clock, a count. It sits in row flow after the texts and NEVER hover-gates:
   *  a fact revealed on hover is a fact the row failed to state, and these are
   *  what let a list drop its hover cards. Its clicks reach the row's own
   *  toggle, so a tooltip inside one is safe. Contrast `trailing`, which is
   *  chrome the row ACTS with (an edit button) and so stays hidden until
   *  hover and swallows its own clicks. */
  rowMeta?: () => React.ReactNode;
  /** Hover-revealed chrome at the row's right edge (an edit button, a badge).
   *  Stays visible while a popup it opened is up (`data-popup-open` pins it),
   *  and its clicks never reach the row's own toggle. */
  trailing?: () => React.ReactNode;
  /**
   * The row's CONTENT in place of the visual + title — for a row whose label
   * is a rendered object (an option's own chip, the way a select lists its
   * values as the chips they become). `title` stays the row's name for search
   * and assistive tech; the body is what the eye reads. Grip, checkbox,
   * trailing chrome and the row's own press are unchanged.
   */
  body?: () => React.ReactNode;
  /**
   * The row ACTS on press instead of naming a selection — the "Create …" row
   * that ends a searched list, the "—" that clears one. It draws no checkbox
   * and no tick, but still RESERVES the checkbox column so its glyph lands on
   * the same rail as every other row.
   *
   * The press runs the ordinary selection path, which is where the host
   * intercepts it: return `false` from `onBeforeSelect` to act and keep the
   * row out of the selection. @default false
   */
  actionRow?: boolean;
  /** Opt this row out of the pool's drag reorder — no grip, no drag. For
   *  synthetic rows (a clear row, a create row) mixed into a reorderable
   *  list. @default true when the pool reorders */
  reorderable?: boolean;
  keywords?: string[];
  disabled?: boolean;
  /** The row LEADS whichever run it is in: first among its section's rows in
   *  the pool, and first in the selected run once chosen — inside its group
   *  either way, never a band of its own (that is `pinnedItems`). For the one
   *  row every pick reaches for (the viewer in a member list). */
  lead?: boolean;
  muted?: boolean;
  invited?: boolean;
  data?: Record<string, unknown>;
}>;

export type EntityBadgeConfig = {
  label: string;
  /** Tooltip on the action button. The label is a compressed noun ("Profile",
   *  "Team"); the tooltip states the verb ("Open Profile"). */
  tooltip?: string;
  /** Default press handler, used when the rendering list passes no
   *  `onItemAction` of its own (a list's explicit handler wins). Lets a pool
   *  carry its action with it — every composition over the pool gets the row
   *  action without per-mount wiring. */
  onAction?: (entity: EntityItemConfig) => void;
  /** Close the selector when the action fires. Set it on an action that opens
   *  another surface (a detail overlay, a dialog) — a picker left open beneath
   *  the surface it launched is answering a question that surface took over.
   *  An `inline` selector has no popover of its own, so a host overlay still
   *  closes itself through its own close callback. */
  closeOnAction?: boolean;
};

export type EntityPoolConfig = Omittable<{
  id: string;
  label: string;
  icon?: IconSource;
  items: EntityItemConfig[];
  /** Items rendered ABOVE the pool's section header, inside this pool's tab —
   *  outside virtual-scroll accounting (`total`/`onLoadRange` ignore them).
   *  Selectable like regular items; filtered by the same search matcher.
   *  E.g. dynamic user references pinned above a members list. A run of
   *  pinned items sharing a `section` is headed by it, the way `label` heads
   *  the list below. */
  pinnedItems?: EntityItemConfig[];
  /** Names the pinned items that carry no `section`, for assistive tech —
   *  they are acts above the list, not members of it. @default label */
  pinnedLabel?: string;
  itemBadge?: EntityBadgeConfig;
  /** Hide avatar/icon column for all items in this pool. @default false */
  hideAvatars?: boolean;
  /**
   * How a run of same-`section` items is headed. `group heading` (default) is the
   * in-list subdivision: uppercased, semibold, a rule between groups — for a
   * list that is MANY groups. `label` heads each group the way the pool's own
   * label heads the whole list (sentence case, medium, faint, the 32px rung)
   * with air instead of a rule above it — for a list that is one vocabulary
   * with one named run inside it. Subsections keep the group heading either way.
   */
  sectionHeadings?: "section-label" | "label";
  /** With `label` headings: a hairline between runs as well — for a list
   *  whose runs are categories (items by type), where air alone reads as
   *  one long column. */
  sectionRules?: boolean;
  /** A trailing control on the FIRST section label — the list's one door to
   *  where its rows are managed (the list's settings), on whichever run
   *  leads. `label` headings only. */
  sectionTrailing?: React.ReactNode;
  /** Total item count for virtual scrolling. When set, the list renders at full height with placeholders for unloaded items. */
  total?: number;
  /** Called when the visible range changes, for loading items on demand. Requires `total` to be set. */
  onLoadRange?: (startIndex: number, endIndex: number) => void;
  /** Called when the search query changes, for loading matching items on demand from the server. */
  onSearchChange?: (query: string) => void;
  /**
   * Skip the client-side `filterEntityItems` pass and render `items` as-is. Set
   * on pools that already filter server-side via `onSearchChange` to avoid
   * double-filtering. @default false
   */
  serverFiltered?: boolean;
  /** The row for an id no loaded page holds — a windowed pool's way to
   *  render a pick past its loaded pages. Undefined when it cannot tell. */
  resolveItem?: (id: string) => EntityItemConfig | undefined;
  /**
   * Make each `section` (and `subsection`) group heading a pressable disclosure that
   * collapses its run of items. For a list subdivided into many sections (e.g.
   * one per integration provider) where a user works in one at a time.
   *
   * A folded section's items stay in the filtered set but leave the KEYBOARD
   * ORDER: a row with no DOM cannot be highlighted or scrolled to, so letting
   * the cursor reach it meant ArrowDown walked into nothing and Enter committed
   * a row the user never saw.
   *
   * Browse and search hold SEPARATE collapse state. A search starts fully
   * expanded (it never inherits what was collapsed while browsing, or matches
   * would hide behind a closed heading and read as "no results"), but the
   * headings stay interactive so a broad result set can still be narrowed. The
   * search-time state is discarded when the query clears.
   * @default false
   */
  collapsibleSections?: boolean;
  /**
   * Sections that start folded (by label), when `collapsibleSections`. Anything
   * omitted starts expanded. Top-level `section` labels only — subsections
   * always start expanded.
   *
   * Applied per LABEL, exactly once each, so a section whose label only appears
   * once an async query resolves still opens folded, while a section the user has
   * since expanded is never re-folded by a later render of the same list.
   */
  defaultCollapsedSections?: string[];
  /**
   * True while `items` is still being fetched. Suppresses the empty state — an
   * unfetched pool is indistinguishable from an empty one by item count alone,
   * and "nothing to select" is a claim about the data, not about the request.
   * @default false
   */
  loading?: boolean;
  /**
   * Drag-to-reorder for the pool's rows. When set (and the list is unfiltered
   * — a search result is not an order), rows grow a leading grip (hover-
   * revealed unless `gripVisibility` says otherwise) and dragging one reports
   * where it landed: `beforeId` is the row it
   * now precedes, null for the end of the list. Ordering semantics stay with
   * the consumer — the pool only reports the gesture.
   */
  onReorder?: (draggedId: string, beforeId: string | null) => void;
  /**
   * When the reorder grip shows. `hover` (default) reveals it as the pointer
   * enters the row; `always` keeps it standing — an authored, ordered list
   * (a field's options) advertises that it can be rearranged, where a search
   * result only admits it on approach. Every row in a reorderable pool keeps
   * the grip gutter either way, so a row that opts out (`reorderable: false`)
   * still starts its content on the same line as its neighbours.
   */
  gripVisibility?: "hover" | "always";
}>;

export type EntitySelectorConfig = Omittable<{
  pools: EntityPoolConfig[];
  defaultPool?: string;
  searchPlaceholder?: string;
  /**
   * Copy for the SEARCHED-empty state: the pool has items, the query matched
   * none of them. @default "No results found"
   */
  emptyMessage?: string;
  /**
   * Copy for the UNSEARCHED-empty state: the pool has nothing to offer at all,
   * so there is no query to relax and "no results" would be a lie. Falls back
   * to `emptyMessage`. @default "Nothing to select"
   */
  noItemsMessage?: string;
  maxSelections?: number;
  /**
   * Item title weight. `medium` (the default) reads as entity rows; `normal`
   * for vocabulary-style pickers whose rows read as a plain list of words
   * rather than named things. @default "medium"
   */
  itemTitleWeight?: "normal" | "medium";
  /**
   * The surface the list paints on, so item hover and selection wash
   * against the right substrate. The `popover` and `drawer` variants are
   * always on that surface; an `inline` selector names whatever its host paints.
   * @default "popover"
   */
  surface?: SurfaceToken;
}>;

export type EntitySelection = {
  id: string;
  poolId: string;
  entity: EntityItemConfig;
};

/** Presentation is explicit per call site: `popover` (default) anchors a
 *  Popover to the trigger, `drawer` presents a bottom drawer, `inline` renders
 *  the list with no overlay at all. There is no automatic mobile swap. */
export type EntitySelectorVariant = "popover" | "inline" | "drawer";

export type EntitySelectorContextValue = {
  config: EntitySelectorConfig;
  variant: EntitySelectorVariant;
  /** The phone sheet's caption — see `EntitySelectorRoot.title`. */
  title?: React.ReactNode;
  selections: EntitySelection[];
  toggleSelection: (entity: EntityItemConfig, poolId: string) => void;
  /**
   * Adds every entity not already selected, in one update. A disabled entity
   * and one the host's `onBeforeSelect` vetoes are skipped rather than
   * closing the picker; a capped selector fills up to its cap and drops the
   * rest. A no-op on a single-select selector, which has no "all".
   */
  selectItems: (entities: EntityItemConfig[], poolId: string) => void;
  /** Removes the named entities from the selection, in one update. */
  deselectItems: (entityIds: string[]) => void;
  isSelected: (entityId: string) => boolean;
  clearSelections: () => void;
  activePool: string;
  setActivePool: (poolId: string) => void;
  search: string;
  setSearch: (search: string) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  getPool: (poolId: string) => EntityPoolConfig | undefined;
  getPoolBadge: (poolId: string) => EntityBadgeConfig | undefined;
  getFilteredItems: (poolId: string) => EntityItemConfig[];
  /**
   * The pool's pinned rows, search-filtered. Computed in the root (not the
   * pool renderer) because pinned rows are part of the keyboard-nav list —
   * they render first, so they occupy nav indices 0..n-1 ahead of
   * `getFilteredItems`.
   */
  getPinnedItems: (poolId: string) => EntityItemConfig[];
  /**
   * Mounts an `EntitySelectorSelected` section; returns the unmount. While one
   * is mounted, every pool omits the entities that section lists: an entity
   * appears ONCE in the popup, in the section that names it as chosen, never
   * again as a ticked row further down. `getFilteredItems` and
   * `getPinnedItems` already reflect the omission.
   */
  registerSelectedSection: () => () => void;
  /**
   * The seat a pool offers the selected section: the node at the top of that
   * pool's scroller. `EntitySelectorSelected` portals its rows into the
   * ACTIVE pool's seat, so chosen and unchosen rows are one list under one
   * scrollbar rather than two stacked scrollers. A pool registers `null` on
   * unmount; with no seat the section renders in place.
   */
  registerSelectedHost: (poolId: string, host: HTMLElement | null) => void;
  getSelectedHost: (poolId: string) => HTMLElement | null;
  /**
   * How many of `poolId`'s loaded items the omission dropped. A virtual pool
   * subtracts it from `total` — those rows ARE loaded, just listed elsewhere,
   * so padding placeholders for them would promise rows that never arrive —
   * and the empty state reads it to tell "nothing here" from "everything
   * here is already chosen".
   */
  getOmittedCount: (poolId: string) => number;
  /**
   * Whether `key` — a section label, or `subsectionKey(section, subsection)` —
   * is folded in `poolId`. Collapse state lives in the ROOT for the same reason
   * pinned rows do: a folded section's rows are not on screen, so the root drops
   * them from the keyboard-nav list too. Held in the pool renderer, the cursor
   * could walk onto a row that produced no DOM and Enter would commit it.
   *
   * Browse and search keep SEPARATE sets. A search must never inherit what was
   * folded while browsing — matches would sit hidden behind a closed heading,
   * reading as "no results" — so search starts fully expanded, and its set is
   * discarded when the query clears so it cannot leak back into browse state.
   */
  isSectionCollapsed: (poolId: string, key: string) => boolean;
  toggleSection: (poolId: string, key: string) => void;
  activeIndex: number;
  activeId: string | null;
  /**
   * Whether the keyboard cursor should PAINT. The cursor itself always exists
   * (it anchors the open-on-selection scroll and gives the arrow keys a start
   * position), but its wash renders only after the keyboard has actually
   * driven it — arrows, or a typed query highlighting the first match.
   * Without the gate, every popup opened with one mid-list row washed at
   * rest, reading as a phantom hover.
   */
  cursorVisible: boolean;
  /** The POINTER moved onto row `index`. Hover is the only caller — keyboard
   *  navigation goes through `moveActive`, and the two are kept apart so the
   *  list can scroll for one and hold still for the other. */
  setActiveIndex: (index: number) => void;
  /**
   * Whether the active row was last set by the pointer rather than the keyboard.
   *
   * Read it before scrolling the active row into view. Keyboard navigation MUST
   * scroll — the next row can be off-screen. Hover must NOT: the row under the
   * cursor is by definition already visible, so "scrolling it into view" only
   * slides the list, which puts a different row under a stationary cursor and
   * can cascade into a second scroll. A partially clipped row at the edge of the
   * list is where it bites, because that is the row a user aims at first.
   */
  isActiveFromPointer: () => boolean;
  moveActive: (delta: number) => void;
  selectActive: () => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
  focusInput: () => void;
};

// Composite key for a subsection. NUL cannot occur in a real label, so it cannot
// collide with a top-level section's key. It is spelled as an ESCAPE and never as
// a literal byte: a literal NUL makes git treat this whole source file as binary,
// which costs every future three-way merge on it.
export function subsectionKey(section: string, subsection: string): string {
  return `${section}\u0000${subsection}`;
}

// Whether `item` sits under a folded heading — its own section, or its
// subsection within an open section. Shared by the root (which drops such rows
// from the keyboard-nav list) and the pool (which does not render them), so the
// two can never disagree about which rows are on screen.
export function isItemCollapsed(
  item: EntityItemConfig | null | undefined,
  isCollapsed: (key: string) => boolean,
): boolean {
  if (!item?.section) return false;
  if (isCollapsed(item.section)) return true;
  return (
    !!item.subsection && isCollapsed(subsectionKey(item.section, item.subsection))
  );
}

export function filterEntityItems(
  items: EntityItemConfig[],
  search: string,
): EntityItemConfig[] {
  const valid = items.filter((item) => item?.id && item?.title);
  if (!search.trim()) return valid;

  const query = search.toLowerCase().trim();
  const tokens = searchWords(query);
  // Each word of the query must BEGIN a word of the title, the description or
  // a keyword (an email splits at its punctuation): "Ale" finds "Alex Reed",
  // never a row whose text merely holds "ale" mid-word ("sales"). A query of
  // punctuation alone has no words, so it falls back to a plain substring.
  return valid.filter((item) => {
    if (item.id.toLowerCase() === query) return true;
    const texts = [item.title, item.description ?? "", ...(item.keywords ?? [])];
    if (!tokens.length) {
      return texts.some((text) => text.toLowerCase().includes(query));
    }
    const words = texts.flatMap(searchWords);
    return tokens.every((token) => words.some((word) => word.startsWith(token)));
  });
}

function searchWords(text: string): string[] {
  return text.toLowerCase().split(/[^\p{L}\p{N}]+/u).filter(Boolean);
}

const EntitySelectorContext =
  React.createContext<EntitySelectorContextValue | null>(null);
EntitySelectorContext.displayName = "EntitySelectorContext";

export function useEntitySelector(): EntitySelectorContextValue {
  const context = React.useContext(EntitySelectorContext);
  if (!context) {
    throw new Error(
      "useEntitySelector must be used within an EntitySelector component.",
    );
  }
  return context;
}

export function useEntitySelectorSelected(entityId: string): boolean {
  const context = useEntitySelector();
  return context.isSelected(entityId);
}

export function useEntitySelectorSelections(): EntitySelection[] {
  const context = useEntitySelector();
  return context.selections;
}

export function useEntitySelectorActivePool(): string {
  const context = useEntitySelector();
  return context.activePool;
}

export function useEntitySelectorSearch(): string {
  const context = useEntitySelector();
  return context.search;
}

export function useEntitySelectorOpen(): boolean {
  const context = useEntitySelector();
  return context.open;
}

type EntitySelectorProviderProps = {
  value: EntitySelectorContextValue;
  children: React.ReactNode;
};

function EntitySelectorProvider({
  value,
  children,
}: EntitySelectorProviderProps) {
  return (
    <EntitySelectorContext.Provider value={value}>
      {children}
    </EntitySelectorContext.Provider>
  );
}

export { EntitySelectorContext, EntitySelectorProvider };
export type { EntitySelectorProviderProps };
